import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, InputNumber, Row } from "antd";
import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import ee from "../../../../services/EventEmitter";
import { CHANGE_TABLE_CELL_EVENT } from "../../../../constants/events";
import {Rounded} from "../../../../helpers/round";

interface Props {
  option: number | null;
  entityKey: string;
  entityId: unknown;
  eventKey?: string;
  round?: number;
}

const Decimal: React.FC<Props> = ({
  option,
  entityKey,
  entityId,
  eventKey = CHANGE_TABLE_CELL_EVENT,
  round = 2,
}) => {
  const [value, setValue] = useState<number | null>(Rounded.of(option, round).value ?? null);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditable) {
      setValue(Rounded.of(option, round).value ?? null);
    }
    // isEditable intentionally omitted: we only want to react to prop changes,
    // not reset user input when they toggle edit mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, round]);

  const onResetHandler = useCallback(() => {
    setIsEditable(false);
    setValue(Rounded.of(option, round).value ?? null);
  }, [option, round]);

  const onSaveHandler = useCallback(() => {
    const toSend: number | null = Rounded.of(value, round).value ?? null;
    ee.emit(eventKey, {
      entityId,
      entityKey,
      value: toSend,
    });

    setValue(toSend);

    setIsEditable(false);
  }, [entityId, entityKey, eventKey, round, value]);

  const onChangeHandler = useCallback((val: number | string | null | undefined) => {
    setValue(val === undefined || val === "" ? null : Number(val));
  }, []);

  if (isEditable) {
    return (
      <Row align="middle">
        <Col>
          <InputNumber
            value={value ?? undefined}
            onChange={onChangeHandler}
            step={0.01}
          />
        </Col>
        <Col>
          <Button type="link" onClick={onSaveHandler} className="single-button">
            <SaveOutlined />
          </Button>
          <Button
            type="link"
            onClick={onResetHandler}
            className="single-button"
          >
            <CloseOutlined />
          </Button>
        </Col>
      </Row>
    );
  }

  return (
    <span>
      {value}
      <Button type="link" onClick={() => setIsEditable(!isEditable)}>
        <EditOutlined />
      </Button>
    </span>
  );
};

export default Decimal;
